import React, { useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { FileUp, FileText, Image as ImgIcon, Download, Trash2, Lock, Sparkles } from "lucide-react";

const ICONS = {
  "application/pdf": FileText,
};

function fmtBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1024/1024).toFixed(1)} MB`;
}

export default function DocumentsVault({ trip, onUpdate }) {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);
  const docs = (trip.documents || []).filter(d => !d.is_deleted);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File over 10 MB limit"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    form.append("label", label);
    try {
      await api.post(`/trips/${trip.trip_id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" }});
      const { data } = await api.get(`/trips/${trip.trip_id}`);
      onUpdate(data); setLabel(""); e.target.value = "";
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const download = async (doc) => {
    try {
      const resp = await api.get(`/trips/${trip.trip_id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Download failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try { const { data } = await api.delete(`/trips/${trip.trip_id}/documents/${id}`); onUpdate(data); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };

  if (!isPro) {
    return (
      <div className="rounded-2xl border border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-10 text-center">
        <Lock size={28} className="mx-auto text-amber-700 mb-3"/>
        <h2 className="font-serif-display text-2xl font-semibold text-stone-900">Document Vault is a Pro feature</h2>
        <p className="text-stone-600 mt-2 max-w-md mx-auto">Upload passport scans, e-tickets, insurance PDFs and more — all attached to this trip and encrypted at rest.</p>
        <Button asChild className="mt-5 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="documents-upgrade-cta">
          <Link to="/upgrade"><Sparkles size={14} className="mr-1.5"/> Upgrade to Pro</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display text-2xl font-semibold">Documents</h2>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="font-mono-code text-[10px] uppercase text-stone-500">Label (optional)</label>
            <Input value={label} onChange={(e)=>setLabel(e.target.value)} placeholder="Passport – Jane Doe" className="mt-1" data-testid="doc-label-input"/>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" onChange={upload} className="hidden" data-testid="doc-file-input"/>
          <Button onClick={()=>fileRef.current?.click()} disabled={busy} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="doc-upload-button">
            <FileUp size={14} className="mr-1"/> {busy ? "Uploading…" : "Upload PDF or image"}
          </Button>
        </div>
        <div className="text-xs text-stone-500 font-mono-code mt-2">PDF · JPG · PNG · WEBP · HEIC · max 10 MB</div>
      </div>

      {docs.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-stone-300 rounded-2xl bg-white text-stone-500">
          <FileText size={26} className="mx-auto text-stone-400 mb-2"/> No documents yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {docs.map(d => {
            const Icon = d.content_type?.startsWith("image/") ? ImgIcon : (ICONS[d.content_type] || FileText);
            return (
              <div key={d.id} className="rounded-2xl border border-stone-200 bg-white p-4 flex items-start gap-3 group" data-testid={`doc-${d.id}`}>
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center"><Icon size={20} className="text-stone-700"/></div>
                <div className="flex-1 min-w-0">
                  {d.label && <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700">{d.label}</div>}
                  <div className="font-semibold truncate">{d.filename}</div>
                  <div className="text-xs text-stone-500 font-mono-code">{fmtBytes(d.size)} · {(d.uploaded_at || "").slice(0,10)}</div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={()=>download(d)} data-testid={`doc-download-${d.id}`} className="p-1.5 text-stone-500 hover:text-[#C85A32]"><Download size={16}/></button>
                  <button onClick={()=>remove(d.id)} data-testid={`doc-delete-${d.id}`} className="p-1.5 text-stone-500 hover:text-red-600"><Trash2 size={16}/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
